import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardShell from '../Dashboard/DashboardShell';
import Pagination from '../components/Pagination';
import { moduleApi, quizApi, quizAnswerApi, userModuleApi } from '../api/api';
import { getUserData, refreshUserProfile } from '../Dashboard/dashboardService';
import { parseQuizResults, QUIZ_PASS_MARK, formatQuizScoreSummary } from './quizUtils';
import './ModuleDetail.css';

const PER_PAGE = 10;

function getListFromResponse(data) {
  const returnData = data?.returnData;
  if (!returnData) return [];
  const listOfItem = returnData.list_of_item ?? returnData;
  if (Array.isArray(listOfItem)) return listOfItem;
  return listOfItem?.data ?? returnData.data ?? [];
}

function getPaginatedMeta(data, perPage) {
  const returnData = data?.returnData || {};
  const listOfItem = returnData.list_of_item || returnData;
  const meta = listOfItem?.meta || returnData.meta || listOfItem || returnData || data;
  const total = Number(meta.total ?? meta.totalCount ?? returnData.total ?? 0) || 0;
  const per = Number(meta.per_page ?? meta.perPage ?? returnData.per_page ?? perPage) || perPage;
  const current = Number(meta.current_page ?? meta.currentPage ?? 1) || 1;
  const last = meta.last_page ?? meta.lastPage ?? (per > 0 ? Math.max(1, Math.ceil(total / per)) : 1);
  return {
    current_page: current,
    last_page: Math.max(1, Number(last) || 1),
    total,
    per_page: per
  };
}

const ModuleQuizListPage = () => {
  const navigate = useNavigate();
  const { moduleId } = useParams();
  const [moduleInfo, setModuleInfo] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0, per_page: PER_PAGE });
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [quizOutcome, setQuizOutcome] = useState(null); // null | 'passed' | 'failed'
  const [lastQuizResult, setLastQuizResult] = useState(null);
  const [hasPreviousAttempt, setHasPreviousAttempt] = useState(false);
  const [moduleCompleteCheckDone, setModuleCompleteCheckDone] = useState(false);

  const id = Number(moduleId);

  const fetchModule = useCallback(async () => {
    if (!id) return;
    try {
      let mod = null;
      let pageNum = 1;
      const perPage = 5;
      while (true) {
        const { data } = await moduleApi.ilist({ paginate: true, per_page: perPage, page: pageNum });
        if (data?.status !== 'OK') break;
        const list = getListFromResponse(data);
        const found = list.find((m) => Number(m.id) === id);
        if (found) {
          mod = found;
          break;
        }
        const rd = data?.returnData || {};
        const metaInfo = rd.list_of_item?.meta ?? rd.meta ?? rd;
        const lastPage = metaInfo?.last_page ?? rd?.last_page ?? 1;
        if (pageNum >= lastPage) break;
        pageNum += 1;
      }
      if (!mod) {
        const { data } = await moduleApi.iget(id);
        if (data?.status === 'OK') {
          const m = data?.returnData?.module ?? data?.returnData ?? data?.module;
          if (m) mod = m;
        }
      }
      if (mod) {
        setModuleInfo({
          id: mod.id,
          name: mod.name ?? mod.module_name ?? mod.title ?? 'Module',
          code: mod.code ?? ''
        });
      } else {
        setModuleInfo(null);
      }
    } catch (_) {
      setModuleInfo(null);
    }
  }, [id]);

  const fetchQuizzes = useCallback(async (pageNum) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await quizApi.ilist({ module_id: id, page: pageNum, per_page: PER_PAGE });
      if (data?.status === 'OK') {
        setQuestions(getListFromResponse(data));
        setMeta(getPaginatedMeta(data, PER_PAGE));
      } else {
        setQuestions([]);
        setError(data?.errorMessage || 'Failed to load quiz.');
      }
    } catch (err) {
      setQuestions([]);
      setError(err.response?.data?.errorMessage || err.message || 'Failed to load quiz.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchModule();
  }, [fetchModule]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        let currentUser = getUserData();
        if (!currentUser?.access_token) {
          setModuleCompleteCheckDone(true);
          return;
        }
        currentUser = (await refreshUserProfile()) ?? currentUser;
        let pageNum = 1;
        const perPage = 50;
        while (true) {
          const params = { per_page: perPage, page: pageNum, paginate: true };
          let { data: umData } = await userModuleApi.ilist(params);
          if (cancelled || umData?.status !== 'OK') break;
          let list = getListFromResponse(umData);
          if (list.length === 0 && currentUser.id) {
            const retry = await userModuleApi.ilist({ ...params, user_id: currentUser.id });
            if (retry.data?.status === 'OK') {
              umData = retry.data;
              list = getListFromResponse(retry.data);
            }
          }
          const forModule = list.filter((row) => {
            const mid = row.module_id ?? row.moduleId ?? row.module?.id;
            return mid != null && Number(mid) === id;
          });
          const enrollment = forModule.find((e) => /complete|completed|finished/.test((e.status || '').toString().toLowerCase())) ?? forModule[0];
          if (enrollment) {
            const status = (enrollment.status ?? enrollment.module?.status ?? 'open').toString().toLowerCase().trim();
            if (/complete|completed|finished/.test(status)) {
              if (!cancelled) navigate(`/modules/${moduleId}/quiz/results`, { replace: true });
              return;
            }
            break;
          }
          const rd = umData?.returnData || {};
          const meta = rd.list_of_item?.meta ?? rd.meta ?? rd;
          const lastPage = meta?.last_page ?? rd?.last_page ?? 1;
          if (pageNum >= lastPage) break;
          pageNum += 1;
        }
        if (!cancelled) setModuleCompleteCheckDone(true);
      } catch (_) {
        if (!cancelled) setModuleCompleteCheckDone(true);
      }
    })();
    return () => { cancelled = true; };
  }, [id, moduleId, navigate]);

  useEffect(() => {
    if (moduleCompleteCheckDone) fetchQuizzes(page);
  }, [page, fetchQuizzes, moduleCompleteCheckDone]);

  useEffect(() => {
    if (!moduleCompleteCheckDone || !id) return;
    (async () => {
      try {
        const user = getUserData();
        const { data } = await quizAnswerApi.iresults({
          module_id: id,
          user_id: user?.id
        });
        if ((data?.status ?? '').toString().toUpperCase() === 'OK') {
          const parsed = parseQuizResults(data);
          if (parsed?.hasAttempt && !parsed.passed) {
            setHasPreviousAttempt(true);
            setLastQuizResult(parsed);
          }
        }
      } catch (_) {}
    })();
  }, [moduleCompleteCheckDone, id]);

  const handleBack = () => navigate(`/modules/${moduleId}`);
  const total = meta.total;
  const lastPage = Math.max(meta.last_page, meta.per_page > 0 && total > 0 ? Math.ceil(total / meta.per_page) : 1);
  const currentPage = meta.current_page;
  const from = total > 0 ? (currentPage - 1) * meta.per_page + 1 : 0;
  const to = Math.min(currentPage * meta.per_page, total);

  const getOptions = (q) => {
    const opts = q.options ?? [];
    return Array.isArray(opts) ? opts : [];
  };

  const getOptionValue = (opt) => (typeof opt === 'object' ? opt.option : opt);
  const getOptionLabel = (opt) => (typeof opt === 'object' ? opt.value ?? opt.option : opt);

  const handleAnswerChange = (questionId, optionValue) => {
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: optionValue }));
    setSubmitMessage(null);
  };

  const handleSubmitAnswers = async () => {
    let user = getUserData();
    if (!user?.access_token) {
      setSubmitMessage({ type: 'error', text: 'Please log in to submit answers.' });
      return;
    }
    user = (await refreshUserProfile()) ?? user;
    if (!user?.id) {
      setSubmitMessage({ type: 'error', text: 'Unable to verify your account. Please log in again.' });
      return;
    }
    const answers = Object.entries(selectedAnswers)
      .filter(([, answer]) => answer != null && answer !== '')
      .map(([questionId, answer]) => ({ question_id: Number(questionId), answer: String(answer) }));
    if (answers.length === 0) {
      setSubmitMessage({ type: 'error', text: 'Please select at least one answer.' });
      return;
    }
    setSubmitting(true);
    setSubmitMessage(null);
    setQuizOutcome(null);
    try {
      const { data } = await quizAnswerApi.iformAction({
        form_method: 'save',
        user_id: Number(user.id),
        module_id: id,
        answers
      });

      const submitOk = (data?.status ?? '').toString().toUpperCase() === 'OK';
      if (!submitOk) {
        setSubmitMessage({ type: 'error', text: data?.errorMessage || 'Failed to submit answers.' });
        return;
      }

      let parsed = parseQuizResults(data);

      try {
        const { data: resultsData } = await quizAnswerApi.iresults({
          module_id: id,
          user_id: Number(user.id)
        });
        if ((resultsData?.status ?? '').toString().toUpperCase() === 'OK') {
          const fromResults = parseQuizResults(resultsData);
          if (fromResults?.hasAttempt) parsed = fromResults;
        }
      } catch (_) {}

      setLastQuizResult(parsed);
      const passed = parsed?.passed === true;
      const summary = formatQuizScoreSummary(parsed);

      if (passed) {
        setQuizOutcome('passed');
        try {
          await userModuleApi.iformAction({
            form_method: 'update',
            user_id: Number(user.id),
            module_id: id,
            status: 'complete'
          });
        } catch (_) {}
        setSubmitMessage({
          type: 'success',
          text: summary ? `Congratulations! You passed the quiz. ${summary}` : 'Congratulations! You passed the quiz.'
        });
      } else {
        setQuizOutcome('failed');
        setHasPreviousAttempt(true);
        setSubmitMessage({
          type: 'error',
          text: summary
            ? `You have scored below the pass mark of ${parsed?.passMark ?? QUIZ_PASS_MARK}%. ${summary}`
            : `You have scored below the pass mark of ${parsed?.passMark ?? QUIZ_PASS_MARK}%. You should re-do the quiz.`
        });
      }
    } catch (err) {
      const msg = err.response?.data?.errorMessage ?? err.message ?? 'Failed to submit answers.';
      setSubmitMessage({ type: 'error', text: Array.isArray(msg) ? msg[0] : msg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewResults = () => {
    navigate(`/modules/${moduleId}/quiz/results`);
  };

  const handleRetakeQuiz = () => {
    setSelectedAnswers({});
    setSubmitMessage(null);
    setQuizOutcome(null);
    setHasPreviousAttempt(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const showResultPanel = quizOutcome === 'failed' || quizOutcome === 'passed';

  const scoreSummary = formatQuizScoreSummary(lastQuizResult);

  const resultPanel = (
    <div className={`module-quiz-fail-banner ${quizOutcome === 'passed' ? 'module-quiz-pass-banner' : ''}`}>
      {scoreSummary ? (
        <p className="module-quiz-score-summary">{scoreSummary}</p>
      ) : lastQuizResult ? (
        <p className="module-quiz-score-summary">
          Score: {lastQuizResult.percentage != null ? `${Number(lastQuizResult.percentage).toFixed(1)}%` : '—'}
          {' · '}Pass mark: {lastQuizResult.passMark ?? QUIZ_PASS_MARK}%
        </p>
      ) : null}
      {quizOutcome === 'failed' && (
        <>
          <p className="module-quiz-fail-text">
            You have scored below the pass mark of {lastQuizResult?.passMark ?? QUIZ_PASS_MARK}%. You should re-do the quiz.
          </p>
          <div className="module-quiz-action-row">
            <button type="button" className="module-quiz-submit-btn module-quiz-retake-btn" onClick={handleRetakeQuiz}>
              Redo quiz
            </button>
            <button type="button" className="module-quiz-submit-btn module-quiz-secondary-btn" onClick={handleViewResults}>
              View your answers
            </button>
          </div>
        </>
      )}
      {quizOutcome === 'passed' && (
        <div className="module-quiz-action-row">
          <button type="button" className="module-quiz-submit-btn" onClick={handleViewResults}>
            View answers and results
          </button>
        </div>
      )}
    </div>
  );

  return (
    <DashboardShell activeMenuId="mymodule">
      <div className="module-page">
        <header className="module-header">
          <div className="module-header-main">
            <button type="button" className="module-back" onClick={handleBack}>
              ← Back to module
            </button>
            <div className="module-header-left">
              <div className="module-logo">HM</div>
              <div className="module-header-text">
                <h1 className="module-title">{moduleInfo?.code ? `${moduleInfo.code} - ${moduleInfo.name}` : (moduleInfo?.name ?? 'Module')}</h1>
                <p className="module-subtitle">Quiz</p>
              </div>
            </div>
          </div>
        </header>

        <section className="module-detail-section">
          <div className="module-detail-panel">
            {error && <p className="management-error">{error}</p>}
            {(!moduleCompleteCheckDone || loading) ? (
              <p className="module-detail-empty">Loading…</p>
            ) : (
              <>
                {hasPreviousAttempt && !showResultPanel && quizOutcome !== 'passed' && (
                  <div className="module-quiz-fail-banner">
                    {scoreSummary && <p className="module-quiz-score-summary">{scoreSummary}</p>}
                    <p className="module-quiz-fail-text">
                      You have scored below the pass mark of {lastQuizResult?.passMark ?? QUIZ_PASS_MARK}%. You should re-do the quiz.
                    </p>
                    <div className="module-quiz-action-row">
                      <button type="button" className="module-quiz-submit-btn module-quiz-retake-btn" onClick={handleRetakeQuiz}>
                        Redo quiz
                      </button>
                      <button type="button" className="module-quiz-submit-btn module-quiz-secondary-btn" onClick={handleViewResults}>
                        View your answers
                      </button>
                    </div>
                  </div>
                )}
                <div className="module-quiz-list">
                  {questions.map((q, idx) => {
                    const qNum = (currentPage - 1) * meta.per_page + idx + 1;
                    const questionId = q.id || `q-${idx}`;
                    const selected = selectedAnswers[questionId];
                    return (
                      <div key={q.id || idx} className="module-quiz-question-card">
                        <p className="module-quiz-question-text">
                          <span className="module-quiz-qn-prefix">Qn{qNum}:</span> {q.question || q.name || 'Question'}
                        </p>
                        <div className="module-quiz-options">
                          {getOptions(q).map((opt, i) => {
                            const value = getOptionValue(opt);
                            const label = getOptionLabel(opt);
                            return (
                              <label key={i} className="module-quiz-option">
                                <input
                                  type="radio"
                                  name={questionId}
                                  value={value}
                                  checked={selected === value}
                                  onChange={() => handleAnswerChange(questionId, value)}
                                />
                                <span>{label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {questions.length === 0 && !loading && (
                  <p className="module-detail-empty">No quiz questions in this module.</p>
                )}
                {submitMessage && !showResultPanel && (
                  <p className={submitMessage.type === 'success' ? 'module-quiz-success' : 'management-error'} style={{ marginTop: '1rem' }}>
                    {submitMessage.text}
                  </p>
                )}
                {showResultPanel && (
                  <div className="module-quiz-fail-banner module-quiz-fail-banner-bottom">
                    {resultPanel}
                  </div>
                )}
                {questions.length > 0 && quizOutcome !== 'failed' && quizOutcome !== 'passed' && (
                  <div className="module-quiz-submit-wrap">
                    <button type="button" className="module-quiz-submit-btn" onClick={handleSubmitAnswers} disabled={submitting}>
                      {submitting ? 'Submitting…' : 'Submit answers'}
                    </button>
                  </div>
                )}
                <Pagination currentPage={currentPage} lastPage={lastPage} onPageChange={setPage} total={total} from={from} to={to} />
              </>
            )}
          </div>
        </section>
      </div>
    </DashboardShell>
  );
};

export default ModuleQuizListPage;
